import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../trpc";
import { getQueryKey } from "@trpc/react-query";
import type { AuthenticationsOutput } from "@zapier/bff";

export function useConvertToManaged() {
  const queryClient = useQueryClient();

  const authenticationsKey = getQueryKey(
    trpc.authentication.authentications,
    undefined,
    "any",
  );
  const connectionStatsKey = getQueryKey(
    trpc.authentication.connectionStats,
    undefined,
    "any",
  );

  const mutation = trpc.authentication.convertToManaged.useMutation({
    onMutate: async ({ id }) => {
      if (!id) {
        return;
      }

      await queryClient.cancelQueries({ queryKey: authenticationsKey });

      // Save the fields per query, so each list gets back its own values
      const previousItems = queryClient
        .getQueriesData<AuthenticationsOutput>({
          queryKey: authenticationsKey,
          type: "active",
        })
        .flatMap(([queryKey, data]) => {
          const item = data?.results?.find((result) => result.id === id);
          return item
            ? [
                {
                  queryKey,
                  id: item.id,
                  isManaged: item.isManaged,
                  customuserId: item.customuserId,
                  permissions: item.permissions,
                },
              ]
            : [];
        });

      queryClient.setQueriesData(
        {
          queryKey: authenticationsKey,
          type: "active",
        },
        (old: AuthenticationsOutput | undefined) => {
          if (old?.results) {
            const updatedAuthenticationsArray = old.results.map((item) =>
              item.id === id
                ? {
                    ...item,
                    isManaged: true,
                    customuserId: null,
                    permissions: {
                      ...item.permissions,
                      convert: false,
                      transfer: false,
                    },
                  }
                : item,
            );

            return {
              ...old,
              results: updatedAuthenticationsArray,
            };
          }

          return old;
        },
      );

      return { previousItems };
    },
    // Restore only the converted connection, so newer changes to other rows stay
    onError: (_error, _variables, context) => {
      for (const { queryKey, id, ...fields } of context?.previousItems ?? []) {
        queryClient.setQueryData<AuthenticationsOutput>(queryKey, (old) => {
          if (!old?.results) {
            return old;
          }

          return {
            ...old,
            results: old.results.map((item) =>
              item.id === id ? { ...item, ...fields } : item,
            ),
          };
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: authenticationsKey });
      queryClient.invalidateQueries({ queryKey: connectionStatsKey });
    },
  });

  return {
    convertToManaged: mutation.mutate,
    convertToManagedAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
  };
}
